import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { toast } from 'react-toastify';
import { formatDateShortRu } from '../utils/dateUtils';
import { formatUserDisplayName, displayGroupRole } from '../utils/userDisplayName';
import DatePickerButton from './DatePickerButton';

const formatDateForDisplay = (isoDate) => {
    if (!isoDate) return '';
    const [y, m, d] = isoDate.split('-');
    return `${d}.${m}.${y}`;
};

const parseDateInput = (str) => {
    if (!str || !str.trim()) return null;
    const s = str.trim().replace(/\//g, '.').replace(/-/g, '.');
    const parts = s.split('.');
    if (parts.length !== 3) return null;
    let d = parseInt(parts[0], 10), m = parseInt(parts[1], 10), y = parseInt(parts[2], 10);
    if (isNaN(d) || isNaN(m) || isNaN(y)) return null;
    if (y < 100) y += 2000;
    if (m < 1 || m > 12) return null;
    const lastDay = new Date(y, m, 0).getDate();
    if (d < 1 || d > lastDay) return null;
    return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
};

const formatDateMask = (digits) => {
    if (!digits || digits.length === 0) return '';
    const d = digits.slice(0, 2);
    const m = digits.length > 2 ? digits.slice(2, 4) : '';
    const y = digits.length > 4 ? digits.slice(4, 8) : '';
    if (!m) return d;
    if (!y) return `${d}.${m}`;
    return `${d}.${m}.${y}`;
};

const applyDateMask = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 8);
    return formatDateMask(digits);
};

const ScheduleForm = ({ event, groupId, onSuccess, onCancel }) => {
    const [loading, setLoading] = useState(false);
    const [trainers, setTrainers] = useState([]);
    const [conflicts, setConflicts] = useState([]);
    const [vacationConflicts, setVacationConflicts] = useState([]);
    const [checkingConflicts, setCheckingConflicts] = useState(false);
    const [displayStartDate, setDisplayStartDate] = useState('');
    const [displayEndDate, setDisplayEndDate] = useState('');

    const [formData, setFormData] = useState({
        cycle: '',
        theme: '',
        start_date: '',
        end_date: '',
        trainers: []
    });

    useEffect(() => {
        loadTrainers();
        if (event) {
            setFormData({
                cycle: event.cycle || '',
                theme: event.theme || '',
                start_date: event.start_date?.split('T')[0] || '',
                end_date: event.end_date?.split('T')[0] || '',
                trainers: event.trainers || []
            });
        }
    }, [event]);

    useEffect(() => {
        setDisplayStartDate(formData.start_date ? formatDateForDisplay(formData.start_date) : '');
    }, [formData.start_date]);

    useEffect(() => {
        setDisplayEndDate(formData.end_date ? formatDateForDisplay(formData.end_date) : '');
    }, [formData.end_date]);

    const loadTrainers = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/users/trainers`, {
                withCredentials: true
            });
            setTrainers(response.data);
        } catch (error) {
            console.error('Ошибка загрузки тренеров:', error);
            toast.error('Ошибка загрузки списка тренеров');
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const addTrainer = () => {
        setFormData(prev => ({
            ...prev,
            trainers: [...prev.trainers, { user_id: '', name: '' }]
        }));
    };

    const removeTrainer = (index) => {
        setFormData(prev => ({
            ...prev,
            trainers: prev.trainers.filter((_, i) => i !== index)
        }));
    };

    const handleTrainerChange = (index, field, value) => {
        const newTrainers = [...formData.trainers];
        newTrainers[index][field] = value;

        // Если изменился user_id, обновляем имя из списка тренеров
        if (field === 'user_id') {
            const trainer = trainers.find(t => t.id == value);
            if (trainer) {
                newTrainers[index].name = formatUserDisplayName(trainer);
            }
        }

        setFormData(prev => ({ ...prev, trainers: newTrainers }));
        
        // Проверяем конфликты после изменения тренеров
        if (field === 'user_id' && value && formData.start_date && formData.end_date) {
            checkConflicts({ ...formData, trainers: newTrainers }).then((payload) => {
                // Если выбранный тренер попал в отпуск — откатываем выбор
                const selectedId = parseInt(value, 10);
                const hasVacation = (payload?.vacation_conflicts || []).some(v => v.trainer_id === selectedId);
                if (hasVacation) {
                    toast.error('Нельзя назначить тренера: у него отпуск на выбранные даты');
                    const reverted = [...newTrainers];
                    reverted[index] = { ...reverted[index], user_id: '', name: '' };
                    setFormData(prev => ({ ...prev, trainers: reverted }));
                }
            });
        }
    };

    const checkConflicts = async (data = formData) => {
        // Проверяем только если есть даты и тренеры
        if (!data.start_date || !data.end_date || !data.trainers || data.trainers.length === 0) {
            setConflicts([]);
            setVacationConflicts([]);
            return { conflicts: [], vacation_conflicts: [] };
        }

        const trainerIds = data.trainers
            .filter(t => t.user_id)
            .map(t => parseInt(t.user_id));

        if (trainerIds.length === 0) {
            setConflicts([]);
            setVacationConflicts([]);
            return { conflicts: [], vacation_conflicts: [] };
        }

        setCheckingConflicts(true);
        try {
            const response = await axios.post(
                `${API_BASE_URL}/schedule/check-conflicts`,
                {
                    trainer_ids: trainerIds,
                    start_date: data.start_date,
                    end_date: data.end_date,
                    event_id: event?.id || null
                },
                { withCredentials: true }
            );

            const payload = {
                conflicts: response.data.conflicts || [],
                vacation_conflicts: response.data.vacation_conflicts || []
            };
            setConflicts(payload.conflicts);
            setVacationConflicts(payload.vacation_conflicts);
            return payload;
        } catch (error) {
            console.error('Ошибка проверки конфликтов:', error);
            return { conflicts: [], vacation_conflicts: [] };
        } finally {
            setCheckingConflicts(false);
        }
    };

    // Проверяем конфликты при изменении дат
    useEffect(() => {
        if (formData.start_date && formData.end_date && formData.trainers.length > 0) {
            const timer = setTimeout(() => {
                checkConflicts();
            }, 500); // Debounce 500ms
            return () => clearTimeout(timer);
        } else {
            setConflicts([]);
            setVacationConflicts([]);
        }
    }, [formData.start_date, formData.end_date]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (vacationConflicts.length > 0) {
            toast.error('Нельзя сохранить: выбранный(е) тренер(ы) в отпуске на эти даты');
            return;
        }
        setLoading(true);

        try {
            const url = event
                ? `${API_BASE_URL}/schedule/${event.id}`
                : `${API_BASE_URL}/schedule`;

            const method = event ? 'put' : 'post';

            await axios[method](url, {
                ...formData,
                group_id: groupId
            }, { withCredentials: true });

            toast.success(event ? 'Событие обновлено' : 'Событие создано');
            onSuccess();
        } catch (error) {
            console.error('Ошибка сохранения события:', error);
            const data = error.response?.data;
            if (error.response?.status === 409 && data?.vacation_conflicts?.length) {
                toast.error(data.message || 'Тренер в отпуске');
                setVacationConflicts(data.vacation_conflicts);
            } else {
                toast.error(data?.message || 'Ошибка сохранения');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="form">
            <div className="form-row">
                <div className="form-group">
                    <label>Номер цикла *</label>
                    <input
                        type="number"
                        name="cycle"
                        value={formData.cycle}
                        onChange={handleChange}
                        required
                        min="1"
                    />
                </div>

                <div className="form-group">
                    <label>Тема встречи *</label>
                    <input
                        type="text"
                        name="theme"
                        value={formData.theme}
                        onChange={handleChange}
                        required
                        placeholder="Название темы встречи"
                    />
                </div>
            </div>

            <div className="form-row">
                <div className="form-group">
                    <label>Дата начала *</label>
                    <div className="date-field-wrapper">
                        <div className="date-field-input-wrap">
                            <input
                                type="text"
                                className="date-field-text"
                                value={displayStartDate}
                                onChange={(e) => setDisplayStartDate(applyDateMask(e.target.value))}
                                onBlur={() => {
                                    const parsed = parseDateInput(displayStartDate);
                                    if (parsed) {
                                        setFormData(prev => ({ ...prev, start_date: parsed }));
                                        setDisplayStartDate(formatDateForDisplay(parsed));
                                    } else if (displayStartDate.trim() === '') {
                                        setFormData(prev => ({ ...prev, start_date: '' }));
                                    }
                                }}
                                title="Введите дату (дд.мм.гггг) или нажмите на календарь"
                            />
                        </div>
                        <div className="date-field-trigger-wrap">
                            <DatePickerButton
                                value={formData.start_date}
                                label="Выбрать дату начала"
                                onChange={(iso) => {
                                    setFormData(prev => ({ ...prev, start_date: iso }));
                                    setDisplayStartDate(formatDateForDisplay(iso));
                                }}
                            />
                        </div>
                    </div>
                </div>

                <div className="form-group">
                    <label>Дата окончания *</label>
                    <div className="date-field-wrapper">
                        <div className="date-field-input-wrap">
                            <input
                                type="text"
                                className="date-field-text"
                                value={displayEndDate}
                                onChange={(e) => setDisplayEndDate(applyDateMask(e.target.value))}
                                onBlur={() => {
                                    const parsed = parseDateInput(displayEndDate);
                                    if (parsed) {
                                        setFormData(prev => ({ ...prev, end_date: parsed }));
                                        setDisplayEndDate(formatDateForDisplay(parsed));
                                    } else if (displayEndDate.trim() === '') {
                                        setFormData(prev => ({ ...prev, end_date: '' }));
                                    }
                                }}
                                title="Введите дату (дд.мм.гггг) или нажмите на календарь"
                            />
                        </div>
                        <div className="date-field-trigger-wrap">
                            <DatePickerButton
                                value={formData.end_date}
                                label="Выбрать дату окончания"
                                onChange={(iso) => {
                                    setFormData(prev => ({ ...prev, end_date: iso }));
                                    setDisplayEndDate(formatDateForDisplay(iso));
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="form-group">
                <label>Тренеры</label>
                {formData.trainers.map((trainer, index) => (
                    <div key={index} className="trainer-row schedule-trainer-row">
                        <select
                            value={trainer.user_id}
                            onChange={(e) => handleTrainerChange(index, 'user_id', e.target.value)}
                            required
                        >
                            <option value="">Выберите тренера</option>
                            {trainers.map(t => (
                                <option key={t.id} value={t.id}>{formatUserDisplayName(t)}</option>
                            ))}
                            {trainer.user_id && !trainers.some(t => String(t.id) === String(trainer.user_id)) && (
                                <option value={trainer.user_id}>{trainer.name || 'Текущий преподаватель'}</option>
                            )}
                        </select>
                        <button type="button" className="btn-icon schedule-trainer-remove" onClick={() => removeTrainer(index)} title="Удалить тренера">
                            ✕
                        </button>
                    </div>
                ))}
                <button type="button" className="btn btn-secondary btn-sm" onClick={addTrainer}>
                    + Добавить тренера
                </button>
            </div>

            {/* Отображение конфликтов */}
            {checkingConflicts && (
                <div style={{ padding: '10px', background: '#f0f0f0', borderRadius: '4px', marginTop: '10px' }}>
                    Проверка конфликтов...
                </div>
            )}

            {!checkingConflicts && conflicts.length > 0 && (
                <div style={{ marginTop: '15px' }}>
                    <div style={{ 
                        padding: '15px', 
                        background: '#fff3cd', 
                        border: '1px solid #ffc107',
                        borderRadius: '8px',
                        marginBottom: '10px'
                    }}>
                        <h4 style={{ margin: '0 0 10px 0', color: '#856404' }}>
                            ⚠️ Обнаружены конфликты расписания
                        </h4>
                        {conflicts.map((conflict, idx) => (
                            <div key={idx} style={{ 
                                marginBottom: '15px', 
                                paddingBottom: '15px',
                                borderBottom: idx < conflicts.length - 1 ? '1px solid #e0e0e0' : 'none'
                            }}>
                                <p style={{ margin: '5px 0', fontWeight: 'bold' }}>
                                    {conflict.trainer_name} уже занят(а)
                                </p>
                                <p style={{ margin: '5px 0', fontSize: '14px' }}>
                                    <strong>Группа:</strong> {conflict.group_nomination || conflict.group_name} 
                                    {conflict.direction_name && ` (${conflict.direction_name})`}
                                </p>
                                <p style={{ margin: '5px 0', fontSize: '14px' }}>
                                    <strong>Событие:</strong> Цикл {conflict.event_cycle} - {conflict.event_theme}
                                </p>
                                <p style={{ margin: '5px 0', fontSize: '14px' }}>
                                    <strong>Даты:</strong> {formatDateShortRu(conflict.event_start_date)} - {formatDateShortRu(conflict.event_end_date)}
                                </p>
                                {conflict.region_name && (
                                    <p style={{ margin: '5px 0', fontSize: '14px' }}>
                                        <strong>Регион:</strong> {conflict.region_name}
                                    </p>
                                )}
                                {conflict.editors && conflict.editors.length > 0 && (
                                    <div style={{ marginTop: '8px', padding: '8px', background: '#fff', borderRadius: '4px' }}>
                                        <p style={{ margin: '0 0 5px 0', fontSize: '13px', fontWeight: 'bold' }}>
                                            Доступ к редактированию этой группы:
                                        </p>
                                        {conflict.editors.map((editor, editorIdx) => (
                                            <p key={editorIdx} style={{ margin: '3px 0', fontSize: '13px', paddingLeft: '10px' }}>
                                                • {formatUserDisplayName(editor) || editor.name} ({displayGroupRole(editor.role)})
                                                {editor.email && ` - ${editor.email}`}
                                            </p>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                        <p style={{ margin: '10px 0 0 0', fontSize: '13px', fontStyle: 'italic', color: '#856404' }}>
                            Вы можете сохранить событие, но рекомендуется согласовать это с менеджерами указанных групп.
                        </p>
                    </div>
                </div>
            )}

            {/* Отпуска (блокирующие) */}
            {!checkingConflicts && vacationConflicts.length > 0 && (
                <div style={{ marginTop: '15px' }}>
                    <div style={{
                        padding: '15px',
                        background: '#f8d7da',
                        border: '1px solid #dc3545',
                        borderRadius: '8px',
                        marginBottom: '10px'
                    }}>
                        <h4 style={{ margin: '0 0 10px 0', color: '#842029' }}>
                            ⛔ Тренер в отпуске
                        </h4>
                        {vacationConflicts.map((c, idx) => (
                            <div key={idx} style={{ marginBottom: '10px' }}>
                                <p style={{ margin: '5px 0', fontWeight: 'bold' }}>
                                    {c.trainer_name}
                                </p>
                                <p style={{ margin: '5px 0', fontSize: '14px' }}>
                                    <strong>Отпуск:</strong> {formatDateShortRu(c.vacation_start_date)} - {formatDateShortRu(c.vacation_end_date)}
                                </p>
                            </div>
                        ))}
                        <p style={{ margin: '10px 0 0 0', fontSize: '13px', fontStyle: 'italic', color: '#842029' }}>
                            Нельзя назначать тренера на даты его отпуска. Измените даты или выберите другого тренера.
                        </p>
                    </div>
                </div>
            )}

            <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={loading}>
                    Отмена
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading || vacationConflicts.length > 0}>
                    {loading ? 'Сохранение...' : (event ? 'Обновить' : 'Создать')}
                </button>
            </div>
        </form>
    );
};

export default ScheduleForm;

